import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchProducts } from "../api";
import "../styles/products.css";

const categoriesData = {
  pharmaceutical: {
    label: "Pharmaceutical Division",
    subcategories: {
      "Machinery": ["Sterile", "Solid", "Semi-Solid", "Liquid", "Auxiliary equipment"],
      "Packaging Materials": [],
      "Active Pharmaceutical Ingredients": []
    }
  },
industrial: {
  label: "Industrial Division",
  subcategories: {
    "Hand Tools": [],
    "Fluid Handling": [],
    "Power Tools": [],
    "Abrasives": [],
    "Blasting Equipment": [],
    "Painting Equipment": [],
    "Material Handling": [],
    "Riveting Tools": []
  }
},
  food: {
    label: "Food Division",
    subcategories: {
      "Quality Control": [],
      "Packaging": [],
      "Processing": [],
      "Consumables": []
    }
  }
};

function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [expandedSubcategory, setExpandedSubcategory] = useState(null);
  const [selectedSubSubcategory, setSelectedSubSubcategory] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);
  const rawCategory = queryParams.get("category");
  const rawSubcategory = queryParams.get("subcategory");
  const subSubcategory = queryParams.get("subsubcategory");

  const getCategory = (category) => {
    const validCategories = Object.keys(categoriesData);
    if (!category || /[\${}\[\]]/.test(category)) return "";
    return validCategories.includes(category) ? category : "";
  };

  const category = getCategory(rawCategory);
  const subcategory = rawSubcategory === null ? "All" : rawSubcategory;

  useEffect(() => {
    if (!category) {
      navigate("/products?category=pharmaceutical");
      return;
    }

    setLoading(true);
    fetchProducts({
      category,
      subcategory: subcategory === "All" ? "" : subcategory,
      subsubcategory: subSubcategory || ""
    })
      .then((data) => {
        setProducts(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [category, subcategory, subSubcategory]);

  useEffect(() => {
    if (category) {
      setExpandedCategory(category);
      if (subcategory && subcategory !== "All") {
        setExpandedSubcategory(subcategory);
      }
      if (subSubcategory) {
        setSelectedSubSubcategory(subSubcategory);
      } else {
        setSelectedSubSubcategory(null);
      }
    }
  }, [category, subcategory, subSubcategory]);

  const handleInquiryClick = () => {
    window.open("https://forms.gle/X2WyJ3wr8FSGC8T96", "_blank");
  };

  const groupBySubcategory = (items) => {
    return items.reduce((groups, product) => {
      const key = product.subcategory || "Others";
      if (!groups[key]) groups[key] = [];
      groups[key].push(product);
      return groups;
    }, {});
  };

  const renderRows = (productsArray) =>
    Array.from({ length: Math.ceil(productsArray.length / 4) }).map((_, rowIndex) => {
      const rowProducts = productsArray.slice(rowIndex * 4, rowIndex * 4 + 4);
      return (
        <ul key={rowIndex} className="product-row">
          {rowProducts.map((product) => (
            <li key={product._id} className="product-item">
              <img
                src={product.image}
                alt={product.name}
                className="product-image"
                loading="lazy"
              />
              <strong className="product-name">{product.name}</strong>
              <button className="inquiry-button" onClick={handleInquiryClick}>
                Get Inquiry
              </button>
            </li>
          ))}
        </ul>
      );
    });

  const hasSubSubcategories = (catKey, subKey) => {
    return categoriesData[catKey]?.subcategories[subKey]?.length > 0;
  };

  const renderTreeSidebar = () => (
    <div className="sidebar">
      {Object.entries(categoriesData).map(([catKey, catData]) => (
        <div key={catKey} className="accordion-section">
          <button
            className={`accordion-button ${category === catKey ? "active" : ""}`}
            onClick={() => {
              const isCollapsing = expandedCategory === catKey;
              if (!isCollapsing) {
                setExpandedCategory(catKey);
                setExpandedSubcategory(null);
                navigate(`/products?category=${catKey}&subcategory=All`);
              } else {
                setExpandedCategory(null);
                setExpandedSubcategory(null);
              }
            }}
          >
            {catData.label}
          </button>

          {expandedCategory === catKey &&
            Object.entries(catData.subcategories).map(([subKey, subSubs]) => (
              <div key={subKey} className="nested-section">
                <button
                  className={`subcategory-button ${subcategory === subKey ? "active" : ""}`}
                  onClick={() => {
                    const newSubcategory = expandedSubcategory === subKey ? null : subKey;
                    if (hasSubSubcategories(catKey, subKey)) {
                      setExpandedSubcategory(newSubcategory);
                    }
                    navigate(`/products?category=${catKey}&subcategory=${encodeURIComponent(newSubcategory || "All")}`);
                  }}
                  data-has-sub-subcategories={hasSubSubcategories(catKey, subKey)}
                >
                  {subKey}
                </button>

                {expandedSubcategory === subKey && subSubs.length > 0 && (
                  <ul className="sub-subcategory-list">
                    {subSubs.map((subSub) => (
                      <li key={subSub}>
                        <button
                          className={`sub-subcategory-button ${subSubcategory === subSub ? "active" : ""}`}
                          onClick={() => {
                            setSelectedSubSubcategory(subSub);
                            navigate(
                              `/products?category=${catKey}&subcategory=${encodeURIComponent(
                                subKey
                              )}&subsubcategory=${encodeURIComponent(subSub)}`
                            );
                          }}
                        >
                          {subSub}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
        </div>
      ))}
    </div>
  );

  return (
    <div className="products-container">
      {renderTreeSidebar()}
      <div className="products-content">
        <h1 className="products-title">
          {category ? categoriesData[category].label : "Products"}
        </h1>
        {loading ? (
          <p className="loading">Loading products...</p>
        ) : products.length > 0 ? (
          <>
            <p className="results-count">{products.length} Results</p>
            {(() => {
              const grouped = groupBySubcategory(products);
              return Object.entries(grouped).map(([group, groupProducts], idx) => {
                if (subcategory === "All" || group === subcategory) {
                  return (
                    <div key={idx} className="subcategory-group">
                      <h2 className="subcategory-group-title">{group}</h2>
                      {renderRows(groupProducts)}
                      <hr className="subcategory-divider" />
                    </div>
                  );
                }
                return null;
              });
            })()}
          </>
        ) : (
          <p className="no-products">No products available at the moment.</p>
        )}
      </div>
    </div>
  );
}

export default Products;
